export type ModuleUiScreenKind = 'list' | 'detail' | 'form' | 'placeholder';

export type ModuleUiScreen = {
    id: string;
    path: string;
    kind: ModuleUiScreenKind;
    title: string;
    navTitle?: string;
    showInNav?: boolean;
    resource?: string;
    icon?: string;
    placeholderTitle?: string;
    placeholderBody?: string;
};

export type ModuleUiDefinition = {
    code: string;
    title: string;
    subtitle: string;
    icon: string;
    primaryResource: string;
    screens: ModuleUiScreen[];
};

function mod(
    code: string,
    title: string,
    subtitle: string,
    icon: string,
    resource: string,
    resourceLabel: string,
    extras: ModuleUiScreen[] = []
): ModuleUiDefinition {
    const base: ModuleUiScreen[] = [
        {
            id: `${resource}-list`,
            path: resource,
            kind: 'list',
            title: resourceLabel,
            navTitle: resourceLabel,
            showInNav: true,
            resource,
            icon: 'heroicons_outline:view-list',
        },
        {
            id: `${resource}-form`,
            path: `${resource}/new`,
            kind: 'form',
            title: `New ${resourceLabel.replace(/s$/, '')}`,
            resource,
        },
        {
            id: `${resource}-detail`,
            path: `${resource}/:id`,
            kind: 'detail',
            title: resourceLabel,
            resource,
        },
    ];
    return { code, title, subtitle, icon, primaryResource: resource, screens: [...base, ...extras] };
}

function ph(id: string, path: string, title: string, body: string, icon = 'heroicons_outline:cog'): ModuleUiScreen {
    return {
        id,
        path,
        kind: 'placeholder',
        title,
        navTitle: title,
        showInNav: true,
        icon,
        placeholderTitle: title,
        placeholderBody: body,
    };
}

export const MODULE_UI_DEFINITIONS: Record<string, ModuleUiDefinition> = {
    hrm: mod(
        'hrm',
        'HR',
        'Warning letters, increment letters, and HR correspondence (UI-only mocks).',
        'heroicons_outline:user-group',
        'letters',
        'Letters',
        [
            ph('employees', 'employees', 'Employees', 'HR policy library and acknowledgements — UI preview only.'),
            ph('settings', 'settings', 'Settings', 'HR module settings bind here after API validation.'),
        ]
    ),
    accounting: mod(
        'accounting',
        'Accounting',
        'Transactions and ledger preview (UI-only mocks).',
        'heroicons_outline:calculator',
        'transactions',
        'Transactions',
        [
            ph('chart-of-accounts', 'chart-of-accounts', 'Chart of accounts', 'Account tree and mappings — preview shell.'),
            ph('settings', 'settings', 'Settings', 'Accounting configuration after validation gate.'),
        ]
    ),
    payroll: mod(
        'payroll',
        'Payroll',
        'Payroll runs and payslips (UI-only mocks).',
        'heroicons_outline:cash',
        'runs',
        'Payroll runs',
        [
            ph('payslips', 'payslips', 'Payslips', 'Employee payslip archive — mock navigation only.'),
            ph('settings', 'settings', 'Settings', 'Payroll rules and tax tables — deferred to API phase.'),
        ]
    ),
    pms: mod(
        'pms',
        'Projects',
        'Project budgets and document attachments (UI-only mocks).',
        'heroicons_outline:clipboard',
        'projects',
        'Projects',
        [
            ph('budgets', 'budgets', 'Budgets', 'Task board and assignments — UI shell.'),
            ph('settings', 'settings', 'Settings', 'Project templates and workflows — API phase.'),
        ]
    ),
    billing: mod(
        'billing',
        'Billing',
        'Invoices, quotes, and POS (UI-only mocks).',
        'heroicons_outline:credit-card',
        'invoices',
        'Invoices',
        [
            ph('quotes', 'quotes', 'Quotes', 'Quote builder preview — no live totals yet.'),
            ph('rentals', 'rentals', 'Rentals', 'Stock pools and hire periods — preview shell.'),
            ph('pos', 'pos', 'POS', 'Point-of-sale register shell for layout review.'),
            ph('settings', 'settings', 'Settings', 'Tax, numbering, and payment methods — after validation.'),
        ]
    ),
    website: mod(
        'website',
        'Websites',
        'Domains and hosted sites (UI-only mocks).',
        'heroicons_outline:globe-alt',
        'sites',
        'Sites',
        [
            {
                id: 'domains-list',
                path: 'domains',
                kind: 'list',
                title: 'Domains',
                navTitle: 'Domains',
                showInNav: true,
                resource: 'domains',
                icon: 'heroicons_outline:globe',
            },
            {
                id: 'domains-detail',
                path: 'domains/:id',
                kind: 'detail',
                title: 'Domain',
                resource: 'domains',
            },
            ph('settings', 'settings', 'Settings', 'Hosting and publish pipelines — API phase.'),
        ]
    ),
    marketing: mod(
        'marketing',
        'Marketing',
        'Campaigns and audiences (UI-only mocks).',
        'heroicons_outline:sparkles',
        'campaigns',
        'Campaigns',
        [
            ph('audiences', 'audiences', 'Audiences', 'Segment builder shell — mock data only.'),
            ph('settings', 'settings', 'Settings', 'Channel credentials and templates — deferred.'),
        ]
    ),
    wallet: mod(
        'wallet',
        'Wallet',
        'Balances and movements (UI-only mocks).',
        'heroicons_outline:currency-dollar',
        'balances',
        'Balances',
        [
            ph('movements', 'movements', 'Movements', 'Ledger movements list shell.'),
            ph('settings', 'settings', 'Settings', 'Wallet limits and currencies — API phase.'),
        ]
    ),
    ai: mod(
        'ai',
        'AI',
        'Drafts, vetting, and metering preview (UI-only mocks).',
        'heroicons_outline:lightning-bolt',
        'sessions',
        'Sessions',
        [
            ph('usage', 'usage', 'Usage', 'Model catalog and routing rules — preview.'),
            ph('settings', 'settings', 'Settings', 'Metering and safety policies — after validation.'),
        ]
    ),
};

export function getModuleUiDefinition(code: string): ModuleUiDefinition {
    const def = MODULE_UI_DEFINITIONS[code];
    if (!def) {
        throw new Error(`Unknown module UI definition: ${code}`);
    }
    return def;
}
